/**
 * Monitor, the hero screen (Redesign v3, UI_SDD §R.3, DEC-061, DEC-187).
 *
 * A header that follows the scan, one banner (headline, stats, today's segmented
 * severity bar and, once finished, the results and actions), the wave rows (Focus) or
 * the dependency graph (Flow), and the side panel (live feed, then top findings).
 * All prior behavioral rules preserved: no auto-navigate, inline messages,
 * warned is not failed, inline stop confirmation with 5s auto-dismiss.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { GitFork, LayoutList, Pause } from 'lucide-react'
import { scansApi } from '../api/scans'
import { subscribeScanEvents } from '../api/events'
import { invalidateScanQueries } from '../hooks/useScanQueries'
import { useScanStore } from '../stores/scanStore'
import { AppShell } from '../components/layout/AppShell'
import { BannerResults, StatusBar } from '../components/monitor/StatusBar'
import { FocusView } from '../components/monitor/FocusView'
import { FlowView } from '../components/monitor/FlowView'
import { LiveRail } from '../components/monitor/LiveRail'
import { plural, runningSentence, SAVED_RUN_NOTE, SAVED_RUN_NOTE_TIMED } from '../components/monitor/SavedRunSummary'
import { AgentPopup } from '../components/monitor/AgentPopup'
import { WhatHappenedPopup } from '../components/scans/WhatHappenedPopup'
import { currentRunSeconds, runTimeValue, shortDuration } from '../utils/runTime'
import { Spinner } from '../components/shared/Spinner'
import { isDone } from '../components/monitor/waveModel'
import { ScanModeBanner } from '../components/ScanModeBanner'
import { SourceManifestNote } from '../components/SourceManifestNote'
import { dateTimeLabel, isUploadScan, repoLabel } from '../utils/format'
import { LlmDecisionModal } from '../components/monitor/LlmDecisionModal'
import type { AgentState } from '../stores/scanStore'
import type { LlmMode, ScanState, ScanStatusResponse } from '../types/api'
import { WAVE_AGENTS } from '../types/api'
import { PageTitle } from '../components/shared/PageTitle'

const ACTIVE_STATES: Set<ScanState> = new Set([
  'queued', 'cloning', 'planning', 'running', 'critic',
] as ScanState[])

/** DEC-171. A scan in one of these is over, and the API saying so is the last word. */
const TERMINAL_STATES: Set<ScanState> = new Set(['complete', 'partial', 'failed'] as ScanState[])

const WAVE_NUMBERS = Object.keys(WAVE_AGENTS).map(Number).sort((a, b) => a - b)

/** DEC-187 decision 1: how much AI the scan used, in the header's words. */
const MODE_TEXT: Record<LlmMode, string> = {
  full: 'AI-assisted scan',
  static_by_choice: 'static-only scan',
  not_configured: 'static-only scan, no AI key',
  awaiting_decision: 'waiting on an AI decision',
  degraded_credits: 'AI lost mid-scan, rest static',
  degraded_auth: 'AI lost mid-scan, rest static',
}

function sourceText(scan: ScanStatusResponse): string {
  if (isUploadScan(scan.repo_url)) return 'Uploaded folder'
  if (scan.repo_url.startsWith('local://')) return 'Server folder'
  const host = scan.repo_url.includes('github.com') ? 'GitHub' : 'Repository'
  return scan.branch ? `${host}, branch ${scan.branch}` : host
}

function timeLabel(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false })
}

/** The wave the banner names: the first with a running agent, else the first with work
 *  left, else the last. */
function currentWave(agents: Record<string, AgentState>): number {
  const list = (n: number) => (WAVE_AGENTS[n] ?? []).map((id) => agents[id]).filter(Boolean)
  const running = WAVE_NUMBERS.find((n) => list(n).some((a) => a.status === 'running'))
  if (running !== undefined) return running
  const pending = WAVE_NUMBERS.find((n) => list(n).some((a) => !isDone(a.status)))
  return pending ?? WAVE_NUMBERS[WAVE_NUMBERS.length - 1] ?? 1
}

const PHASE_TEXT: Partial<Record<ScanState, string>> = {
  queued: 'Queued',
  cloning: 'Scanning · Preparing repository',
  planning: 'Scanning · Planner routing agents',
  critic: 'Scanning · Critic consolidating findings',
}

export default function MonitorPage() {
  const { scanId } = useParams<{ scanId: string }>()
  const navigate = useNavigate()
  const {
    initScan,
    handleEvent,
    scanState,
    agents,
    liveFindings,
    criticSummary,
    budgetWarning,
    progress,
    streamComplete,
    severityCounts,
    llmUnavailable,
    clearLlmUnavailable,
    agentEventsSeen,
    fromSaved,
    hydrateFromSaved,
    setSavedSeverity,
    setReviewedSeverity,
    waveRunSeconds,
    waveStartedAt,
  } = useScanStore()

  const [view, setView] = useState<'focus' | 'flow'>('focus')
  const [sseError, setSseError] = useState(false)
  const [nowTick, setNowTick] = useState(() => Date.now())
  const [showStopConfirm, setShowStopConfirm] = useState(false)
  const [decisionDismissed, setDecisionDismissed] = useState(false)
  // DEC-197: the agent pop-up and the scan's "What happened" pop-up.
  const [inspectId, setInspectId] = useState<string | null>(null)
  const [storyOpen, setStoryOpen] = useState(false)
  const cleanupRef = useRef<(() => void) | null>(null)
  const queryClient = useQueryClient()

  const { data: scan, isLoading, dataUpdatedAt } = useQuery({
    queryKey: ['scan', scanId],
    queryFn: () => scansApi.get(scanId!),
    enabled: !!scanId,
    refetchInterval: streamComplete ? false : 5000,
  })

  const refreshScan = () => queryClient.invalidateQueries({ queryKey: ['scan', scanId] })

  const pauseMutation = useMutation({
    mutationFn: () => scansApi.pause(scanId!),
    onSettled: refreshScan,
  })
  const resumeMutation = useMutation({
    mutationFn: () => scansApi.resume(scanId!),
    onSettled: refreshScan,
  })
  // DEC-171: "Run again" on a retryable failure. Starts a NEW scan on the same repo and
  // branch rather than reviving this row, because a scan is a point-in-time record and
  // the failed one stays as history.
  const rerunMutation = useMutation({
    mutationFn: () =>
      scansApi.create({
        repo_url: scan!.repo_url,
        branch: scan!.branch,
        triggered_by: 'manual',
      } as never),
    onSuccess: (created) => {
      // DEC-123: over the concurrency limit a scan is queued rather than refused and has
      // no id yet. Staying put is correct then; navigating nowhere is not.
      if (created?.scan_id) navigate(`/scans/${created.scan_id}/monitor`)
    },
  })
  // DEC-067/DEC-070: continue static-only after the LLM-unavailable pause.
  // The modal closes the moment the user decides; the scan query is refreshed
  // whether the call succeeds or 409s (scan already moved on), so the modal
  // can never re-open from stale cached state.
  const continueStaticMutation = useMutation({
    mutationFn: () => scansApi.resume(scanId!, true),
    onMutate: () => setDecisionDismissed(true),
    onSuccess: () => clearLlmUnavailable(),
    onSettled: refreshScan,
  })
  const stopMutation = useMutation({
    mutationFn: () => scansApi.deleteScan(scanId!),
    onSuccess: () => navigate('/scans'),
  })

  const handleStopConfirm = useCallback(() => {
    setShowStopConfirm(false)
    stopMutation.mutate()
  }, [stopMutation])

  // SSE + clock
  useEffect(() => {
    if (!scanId) return
    initScan(scanId)
    const cleanup = subscribeScanEvents(
      scanId,
      (event) => handleEvent(event),
      () => setSseError(true),
      // DEC-191 decision 5: a scan that ends changes every list and the sidebar badge,
      // not only this page's own scan.
      () => { void invalidateScanQueries(queryClient) },
    )
    cleanupRef.current = cleanup
    const timer = setInterval(() => setNowTick(Date.now()), 1000)
    return () => {
      cleanup()
      clearInterval(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanId])

  // DEC-171: a terminal state from the API outranks the live one.
  //
  // The live stream is an optimisation, not the source of truth, and SSE is lossy: it
  // reconnects, it drops, and a terminal event can be missed. `initScan` seeds
  // scanState='queued', so it is never null here and `scanState ?? scan?.state` could
  // never fall through -- a scan that died in a second displayed as Queued with a running
  // clock 84 minutes later, beside a sidebar that read `failed`. Preferring the API once
  // it reports an outcome makes the page self-heal within one poll, whatever went wrong
  // in transport.
  const apiState = scan?.state ?? null
  const effectiveState =
    apiState !== null && TERMINAL_STATES.has(apiState) ? apiState : (scanState ?? apiState)
  const isActive = effectiveState !== null && ACTIVE_STATES.has(effectiveState)
  const isPaused = effectiveState === 'paused'
  const isTerminalSuccess = effectiveState === 'complete' || effectiveState === 'partial'
  const isFailed = effectiveState === 'failed'
  // DEC-171: a stop is a choice, not a fault, and reads differently.
  const wasStopped = scan?.failure_code === 'scan_stopped_by_user'
  // DEC-197: the stop line tells the truth about what was kept.
  const failureMessage = wasStopped
    ? (scan?.finding_count ?? 0) > 0
      ? 'Stopped. Findings saved so far are kept.'
      : 'Scan was stopped. All progress has been discarded.'
    : (scan?.failure_message ??
       'An error occurred. See what happened for the details.')
  const isTerminal = isTerminalSuccess || isFailed

  // DEC-185: a finished scan whose stream carried no agent events (the API restarted
  // since it ran, so the in-memory bus has nothing) is rebuilt from what was saved.
  // Gated on the stream having ended (or failed) so a same-process replay, whose agent
  // events arrive before its terminal event, is never overwritten by the saved copy.
  const needsSaved =
    !!scanId &&
    apiState !== null &&
    TERMINAL_STATES.has(apiState) &&
    !agentEventsSeen &&
    (streamComplete || sseError)
  const { data: savedAgents } = useQuery({
    queryKey: ['scan-agents', scanId],
    queryFn: () => scansApi.agents(scanId!),
    enabled: needsSaved,
    staleTime: Infinity,
  })
  // The severity bar from saved findings: the report's summary, the same query key the
  // Findings page uses, so no new endpoint and no second fetch when both are open.
  // DEC-187: a finished scan also reads its top findings and High count from here.
  const {
    data: savedReport,
    isError: savedReportFailed,
  } = useQuery({
    queryKey: ['report', scanId],
    queryFn: () => scansApi.report(scanId!),
    enabled: !!scanId && (needsSaved || fromSaved || isTerminalSuccess),
    staleTime: 60_000,
  })
  useEffect(() => {
    if (savedAgents) hydrateFromSaved(savedAgents.agents, savedAgents.dead_files ?? null)
  }, [savedAgents, hydrateFromSaved])
  useEffect(() => {
    if (fromSaved && savedReport) setSavedSeverity(savedReport.summary.findings_by_severity)
  }, [fromSaved, savedReport, setSavedSeverity])
  // DEC-187: once a scan finishes, every figure on the banner is the reviewed one. The
  // live bar counted raw findings before the Critic merged and suppressed some, so it
  // switches to the report's counts, matching the headline and the Critic line.
  useEffect(() => {
    if (!fromSaved && isTerminalSuccess && savedReport) setReviewedSeverity(savedReport.summary.findings_by_severity)
  }, [fromSaved, isTerminalSuccess, savedReport, setReviewedSeverity])
  // The scan row was last polled while running; read it once more when the stream ends,
  // so the finding count is the saved one rather than the live total.
  useEffect(() => {
    if (streamComplete && scanId) queryClient.invalidateQueries({ queryKey: ['scan', scanId] })
  }, [streamComplete, scanId, queryClient])

  // DEC-195: run time, every wait for a person taken out. The server sends it; between
  // polls it ticks, unless the scan is over or waiting. A server that predates it sends
  // no field: a live scan then falls back to the wall clock, and a finished one reads
  // "not recorded" rather than a number that counts the pauses.
  const runSeconds: number | null = scan
    ? scan.run_seconds !== undefined
      ? currentRunSeconds(scan, { terminal: isTerminal, fetchedAt: dataUpdatedAt || nowTick, now: nowTick, paused: isPaused })
      : isTerminal
        ? null
        : Math.max(0, (nowTick - new Date(scan.started_at).getTime()) / 1000)
    : 0
  const runPartial = !!scan?.run_time_is_partial
  const waitingForPerson = !isTerminal && (!!scan?.waiting_for_person || isPaused)
  const ranText = runSeconds === null ? 'run time not recorded' : `ran ${runPartial ? 'at least ' : ''}${shortDuration(runSeconds)}`

  // DEC-195: each wave's run time. Saved timing first, then wave_finished events, then a
  // running wave's own clock from its first agent's start (it stops while waiting).
  const waveTimes: Record<number, number | null> = {}
  for (const w of scan?.timing?.waves ?? []) waveTimes[w.wave] = w.run_seconds
  for (const [n, s] of Object.entries(savedAgents?.wave_run_seconds ?? {})) waveTimes[Number(n)] = s
  for (const [n, s] of Object.entries(waveRunSeconds)) if (s !== null && s !== undefined) waveTimes[Number(n)] = s
  if (!isTerminal && !waitingForPerson) {
    for (const [n, at] of Object.entries(waveStartedAt)) {
      const num = Number(n)
      if (waveTimes[num] !== undefined) continue
      const wave = (WAVE_AGENTS[num] ?? []).map((id) => agents[id]).filter(Boolean)
      if (wave.some((a) => a.status === 'running')) waveTimes[num] = Math.max(0, (nowTick - new Date(at).getTime()) / 1000)
    }
  }
  const criticFailed =
    !!scan?.agents_failed?.includes('critic') ||
    !!scan?.agent_failures?.some((f) => f.agent_id === 'critic') ||
    !!savedAgents?.critic_failure

  const agentList = Object.values(agents)
  // DEC-187: skipped counts as finished, and the subtitle says how many were skipped.
  const agentsTotal = agentList.length
  const agentsDone = agentList.filter((a) => isDone(a.status)).length
  const agentsSkipped = agentList.filter((a) => a.status === 'skipped').length
  const liveTokens = agentList.reduce((s, a) => s + a.tokens_used, 0)
  const tokensUsed = isTerminal && scan?.total_tokens_used ? scan.total_tokens_used : liveTokens
  const agentsFailedCount = agentList.filter((a) => a.status === 'failed').length
  const findingCount = scan?.finding_count && isTerminal
    ? scan.finding_count
    : agentList.reduce((s, a) => s + a.findings_count, 0)

  // DEC-067/DEC-070: show the decision modal only while the scan is actually
  // paused. The SSE stream replays from event 0 on reconnect, so a historical
  // llm_unavailable event from an already-decided scan must never resurrect
  // the modal — the paused state is the source of truth that a decision is
  // still pending.
  const awaitingDecision =
    isPaused &&
    ((llmUnavailable && llmUnavailable.decision === 'awaiting_user') ||
      scan?.llm_mode === 'awaiting_decision')
  const decisionReason = llmUnavailable?.reason ?? scan?.llm_degraded_reason ?? 'credit_exhausted'
  const decisionAgent = llmUnavailable?.from_agent ?? scan?.llm_degraded_from_agent ?? ''

  // DEC-070: a NEW llm_unavailable event re-arms the modal; a decision or
  // dismissal otherwise sticks for the rest of the visit.
  useEffect(() => {
    if (llmUnavailable) setDecisionDismissed(false)
  }, [llmUnavailable])

  // ---- Header and banner copy (DEC-187 decisions 1 and 2) ----
  const repo = scan ? repoLabel(scan.repo_url) : ''
  const modeText = scan?.llm_mode ? MODE_TEXT[scan.llm_mode] : null
  const headerTitle = isTerminal ? repo || 'Scan results' : repo ? `Scanning ${repo}` : 'Scanning'
  const headerSubtitle = scan
    ? [
        sourceText(scan),
        isTerminal && scan.completed_at
          ? `finished ${dateTimeLabel(scan.completed_at)}`
          : `started ${timeLabel(scan.started_at)}`,
        modeText,
      ]
        .filter(Boolean)
        .join(' · ')
    : ''

  // The High count of a finished scan: the report's (after review) once it is here,
  // otherwise the live count, and never a guess on a saved scan.
  const reportHigh = savedReport?.summary?.findings_by_severity?.high
  const highCount: number | null =
    typeof reportHigh === 'number' ? reportHigh : fromSaved ? null : severityCounts.high
  const findingsPhrase = `${plural(findingCount, 'finding')}${highCount === null ? '' : `, ${highCount} High`}`
  const finishedPhrase = scan?.completed_at
    ? `Finished ${dateTimeLabel(scan.completed_at)} · ${ranText}`
    : ranText.charAt(0).toUpperCase() + ranText.slice(1)
  const finishedCount = `${agentsDone} of ${agentsTotal} agents finished${
    agentsSkipped > 0 ? `, ${agentsSkipped} of them skipped` : ''
  }`
  const waveNow = currentWave(agents)
  const runningLabels = WAVE_NUMBERS.flatMap((n) => WAVE_AGENTS[n] ?? [])
    .map((id) => agents[id])
    .filter((a): a is AgentState => !!a && a.status === 'running')
    .map((a) => a.label)

  let bannerHeadline: string
  let bannerSubtitle: string
  if (effectiveState === 'complete') {
    bannerHeadline = `Scan complete · ${findingsPhrase}`
    bannerSubtitle = finishedPhrase
  } else if (effectiveState === 'partial') {
    bannerHeadline = `Finished with errors · ${findingsPhrase}`
    bannerSubtitle = finishedPhrase
  } else if (isFailed) {
    bannerHeadline = wasStopped ? 'Stopped' : 'Scan failed'
    bannerSubtitle = scan?.completed_at
      ? `Ended ${dateTimeLabel(scan.completed_at)} · ${ranText}`
      : finishedCount
  } else if (isPaused) {
    bannerHeadline = `Paused · Wave ${waveNow} of ${WAVE_NUMBERS.length}`
    bannerSubtitle = [runningSentence(runningLabels, true), finishedCount].filter(Boolean).join(' · ')
  } else if (effectiveState === 'running') {
    bannerHeadline = `Scanning · Wave ${waveNow} of ${WAVE_NUMBERS.length}`
    bannerSubtitle = [runningSentence(runningLabels), finishedCount].filter(Boolean).join(' · ')
  } else {
    bannerHeadline = (effectiveState && PHASE_TEXT[effectiveState]) ?? 'Scanning'
    bannerSubtitle = finishedCount
  }

  if (isLoading) {
    return (
      <AppShell scanId={scanId} crumb="Live monitor" active="monitor">
        <div className="h-full flex items-center justify-center">
          <Spinner />
        </div>
      </AppShell>
    )
  }

  return (
    <AppShell scanId={scanId} crumb={isTerminal ? 'Scan results' : 'Live monitor'} active="monitor">
      <div className="px-7 py-[22px] max-w-[1240px]">
        <ScanModeBanner
          llmMode={scan?.llm_mode}
          reason={scan?.llm_degraded_reason}
          fromAgent={scan?.llm_degraded_from_agent}
        />
        <SourceManifestNote manifest={scan?.source_manifest} />
        {/* Header + view toggle: it follows the scan (DEC-187 decision 1) */}
        <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
          <div className="min-w-0">
            <PageTitle eyebrow={isTerminal ? 'Scan results' : 'Live monitor'}>{headerTitle}</PageTitle>
            {headerSubtitle && (
              <p data-testid="monitor-subtitle" className="text-[13px] leading-[1.45] text-muted mt-2 mb-0">
                {headerSubtitle}
              </p>
            )}
          </div>
          <div className="flex items-center bg-ground border border-hairline rounded-md p-[3px] gap-[2px]">
            {(
              [
                { id: 'focus', label: 'Focus', Icon: LayoutList },
                { id: 'flow', label: 'Flow', Icon: GitFork },
              ] as const
            ).map(({ id, label, Icon }) => (
              <button
                key={id}
                onClick={() => setView(id)}
                className={`flex items-center gap-[6px] font-semibold text-[11px] rounded-sm px-3 py-[6px] transition-colors ${
                  view === id ? 'bg-surface-active text-ink' : 'text-muted hover:text-body'
                }`}
              >
                <Icon className="w-[13px] h-[13px]" /> {label}
              </button>
            ))}
          </div>
        </div>

        <StatusBar
          state={effectiveState}
          headline={bannerHeadline}
          subtitle={bannerSubtitle}
          note={
            fromSaved
              ? Object.values(agents).some((a) => a.timeRecorded && a.status !== 'waiting')
                ? SAVED_RUN_NOTE_TIMED
                : SAVED_RUN_NOTE
              : undefined
          }
          agentsDone={agentsDone}
          agentsTotal={agentsTotal}
          progress={progress}
          runTime={runTimeValue(runSeconds, runPartial)}
          waitingForPerson={waitingForPerson && !isTerminal}
          tokensUsed={tokensUsed}
          severityCounts={severityCounts}
          budgetWarning={budgetWarning}
          tokenBudget={scan?.token_budget ?? null}
          isActive={isActive}
          isPaused={isPaused}
          onPause={() => pauseMutation.mutate()}
          onResume={() => resumeMutation.mutate()}
          onStopRequest={() => setShowStopConfirm(true)}
          stopConfirmOpen={showStopConfirm}
          onStopConfirm={handleStopConfirm}
          onStopDismiss={() => setShowStopConfirm(false)}
          pausePending={pauseMutation.isPending}
          resumePending={resumeMutation.isPending}
          emptySeverityText={
            !fromSaved
              ? undefined
              : savedReport
                ? 'No findings after review.'
                : savedReportFailed
                  ? 'Severity counts could not be loaded.'
                  : 'Loading saved findings…'
          }
          results={
            isTerminalSuccess && scanId ? (
              <BannerResults
                scanId={scanId}
                state={effectiveState!}
                findingCount={findingCount}
                agentsFailedCount={agentsFailedCount}
                criticSummary={criticSummary}
              />
            ) : undefined
          }
        />

        {/* Paused explanatory note (§R.3 / DEC-040) */}
        {isPaused && (
          <div className="flex gap-[10px] bg-chip-warn border border-warn/30 rounded-md px-[14px] py-[10px] mb-[14px]">
            <Pause className="w-[15px] h-[15px] text-warn flex-none mt-[1px]" />
            <div className="text-[12px] leading-[1.5] text-warn">
              <b className="font-semibold">Scan paused.</b> Agents that completed kept their findings. Resume
              continues from the current wave's first incomplete agent. Run time does not count while it is paused.
            </div>
          </div>
        )}

        {/* SSE reconnect note */}
        {sseError && !isTerminal && (
          <div className="text-[12px] text-body bg-fill-subtle border border-hairline rounded-md px-[14px] py-2 mb-[14px]">
            Live updates disconnected, reconnecting automatically…
          </div>
        )}

        {/* Failed and stopped keep their red box (DEC-187 decision 2) */}
        {isFailed && (
          <div className="bg-panel border border-critical/30 rounded-lg shadow-card px-[22px] py-5 mb-4">
            <div className="font-semibold text-[15px] text-critical mb-1">
              {wasStopped ? 'Scan stopped' : 'Scan failed'}
            </div>
            <p className="text-[12px] text-body mb-3">{failureMessage}</p>
            {/* DEC-171: what to change, when there is something to change. Shown instead
                of a retry, not beside it: a dead end with an instruction is not a dead
                end, and a retry that cannot succeed is worse than none. */}
            {scan?.failure_remedy && (
              <p className="text-[12px] text-muted mb-3">{scan.failure_remedy}</p>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              {/* DEC-197: the full story, one click away, so the page gets no busier. */}
              {!wasStopped && (
                <button
                  onClick={() => setStoryOpen(true)}
                  aria-haspopup="dialog"
                  className="font-semibold text-[12px] text-white bg-accent rounded-md px-[18px] py-[9px] hover:opacity-90 transition-opacity"
                >
                  See what happened
                </button>
              )}
              {/* Only where retrying can plausibly succeed (SDD 17.9). Classified
                  server-side and defaulting to false, so "we do not know" never renders
                  as "try again". */}
              {/* DEC-180: an uploaded folder is not kept, so it can never be re-run from
                  here. The way forward is to pick it again. */}
              {isUploadScan(scan?.repo_url) && (
                <button
                  onClick={() => navigate('/connect?mode=upload')}
                  className="font-semibold text-[12px] text-ink bg-ground border border-hairline rounded-md px-[18px] py-[9px] hover:bg-hover-hue transition-colors"
                >
                  Upload again
                </button>
              )}
              {scan?.failure_is_retryable && !isUploadScan(scan?.repo_url) && (
                <button
                  onClick={() => rerunMutation.mutate()}
                  disabled={rerunMutation.isPending}
                  className="font-semibold text-[12px] text-ink bg-ground border border-hairline rounded-md px-[18px] py-[9px] hover:bg-hover-hue transition-colors disabled:opacity-50"
                >
                  {rerunMutation.isPending ? 'Starting…' : 'Run again'}
                </button>
              )}
              <button
                onClick={() => navigate('/connect')}
                className="font-semibold text-[12px] text-body bg-ground border border-hairline rounded-md px-[18px] py-[9px] hover:bg-hover-hue transition-colors"
              >
                New scan
              </button>
            </div>
          </div>
        )}

        {/* Waves + live rail. The Flow graph takes the full width (DEC-188), so the rail is Focus only. */}
        <div className="flex gap-[14px] items-start">
          <div className="flex-1 min-w-0">
            {view === 'focus' ? (
              <FocusView
                agents={agents}
                scanId={scanId!}
                isPaused={isPaused}
                fromSaved={fromSaved}
                finished={isTerminal}
                waveTimes={waveTimes}
                onInspect={setInspectId}
              />
            ) : (
              <FlowView
                agents={agents}
                scanId={scanId!}
                fromSaved={fromSaved}
                apiState={effectiveState}
                criticFailed={criticFailed}
                onInspect={setInspectId}
              />
            )}
          </div>
          {view === 'focus' && <LiveRail
            findings={liveFindings}
            frozen={isTerminal}
            emptyText={
              fromSaved
                ? 'The live feed is not kept after a restart. Every saved finding is on the findings page.'
                : undefined
            }
            top={
              isTerminalSuccess && scanId
                ? {
                    scanId,
                    findings: savedReport?.top_findings,
                    failed: savedReportFailed,
                    total: findingCount,
                  }
                : undefined
            }
          />}
        </div>
      </div>

      {/* DEC-197: the agent pop-up, from a wave row or a Flow node */}
      {inspectId && agents[inspectId] && scanId && (
        <AgentPopup
          agent={agents[inspectId]}
          scanId={scanId}
          finished={isTerminal}
          fromSaved={fromSaved}
          onClose={() => setInspectId(null)}
          onRerun={scan && !isUploadScan(scan.repo_url) ? () => rerunMutation.mutate() : undefined}
          rerunPending={rerunMutation.isPending}
        />
      )}
      {storyOpen && scan && (
        <WhatHappenedPopup scan={scan} onClose={() => setStoryOpen(false)} />
      )}

      {/* DEC-067: LLM-unavailable decision modal — user controls the trade-off */}
      {awaitingDecision && !decisionDismissed && (
        <LlmDecisionModal
          reason={decisionReason}
          fromAgent={decisionAgent}
          busy={continueStaticMutation.isPending}
          onContinueStatic={() => continueStaticMutation.mutate()}
          onStayPaused={() => setDecisionDismissed(true)}
        />
      )}
    </AppShell>
  )
}
